import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';

import {
  GET_ACCOUNT_NOTIFICATIONS,
  MARK_ACCOUNT_NOTIFICATIONS_READ,
  NEW_ACCOUNT_NOTIFICATION_SUBSCRIPTION,
} from '@shared/api/graphql/notifications';
import { useMockAccountNotifications } from '@shared/hooks/use-mock-account-notifications';
import {
  categorizeNotification,
  type NotificationCategory,
} from '@shared/lib/notifications/categorize';
import { useTypedSubscription } from '@shared/lib/ws';
import { useToastStore } from '@shared/stores/toast.store';

export interface AccountNotification {
  id: string;
  title: string | null;
  description: string | null;
  url: string | null;
  externalId: string | null;
  viewed: boolean;
  createdAt: string | null;
  receivedDate: string | null;
  type: { id: string } | null;
}

interface AccountNotificationsQueryData {
  account: {
    notification: {
      paginatedList: {
        items: AccountNotification[];
        pageInfo: { hasNextPage: boolean; page: number; perPage: number; totalItems: number };
      };
      unread: AccountNotification[];
    } | null;
  } | null;
}

interface AccountNotificationsQueryVariables {
  page: number;
  perPage: number;
}

interface MarkNotificationsReadData {
  account: {
    notification: {
      markRead: { status: string; itemIds: string[]; errors: { message: string }[] | null };
    } | null;
  } | null;
}

interface MarkNotificationsReadVariables {
  ids: string[];
}

interface NewAccountNotificationSubscriptionData {
  newAccountNotification: AccountNotification;
}

export interface AccountNotificationsState {
  items: AccountNotification[];
  unreadCount: number;
  fetching: boolean;
  error: Error | undefined;
  markAsRead: (ids: string[]) => void;
  markAllAsRead: () => void;
  refetch: () => void;
}

export type NotificationFilter = 'all' | 'unread' | NotificationCategory;

const PER_PAGE = 30;

/** Sorts newest-first by `createdAt`, falling back to `receivedDate` when absent. */
function timestampOf(n: AccountNotification): number {
  const raw = n.createdAt ?? n.receivedDate;
  return raw ? new Date(raw).getTime() : 0;
}

/**
 * Backs the notification centre (DEV-3856): fetches recent + unread `AccountNotification`s,
 * merges in real-time pushes from the `newAccountNotification` subscription, scopes the result
 * to Client Requests and Vault (see `categorizeNotification`), and layers an optimistic
 * "read" overlay on top since `markRead`'s response doesn't return updated items for the cache
 * to normalize automatically.
 */
export function useRemoteAccountNotifications(): AccountNotificationsState {
  const [{ data, fetching, error }, reexecuteQuery] = useQuery<
    AccountNotificationsQueryData,
    AccountNotificationsQueryVariables
  >({
    query: GET_ACCOUNT_NOTIFICATIONS,
    variables: { page: 1, perPage: PER_PAGE },
    requestPolicy: 'cache-and-network',
  });

  const [, markReadMutation] = useMutation<
    MarkNotificationsReadData,
    MarkNotificationsReadVariables
  >(MARK_ACCOUNT_NOTIFICATIONS_READ);

  const { data: subData } = useTypedSubscription<NewAccountNotificationSubscriptionData>(
    NEW_ACCOUNT_NOTIFICATION_SUBSCRIPTION,
  );

  const [pushed, setPushed] = useState<AccountNotification[]>([]);
  const [locallyReadIds, setLocallyReadIds] = useState<Set<string>>(new Set());

  const { t } = useTranslation('notifications');
  const addToast = useToastStore((s) => s.addToast);
  // Tracks ids we've already toasted for, independent of `pushed` — the subscription can
  // re-deliver the same item across reconnects, and toasting off a setState updater would run
  // the side effect on every React re-invocation of that updater (e.g. Strict Mode).
  const toastedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const item = subData?.newAccountNotification;
    if (!item || toastedIdsRef.current.has(item.id)) return;
    toastedIdsRef.current.add(item.id);

    setPushed((prev) => (prev.some((p) => p.id === item.id) ? prev : [item, ...prev]));

    if (categorizeNotification(item)) {
      addToast({
        variant: 'info',
        title: item.title ?? t('title'),
        description: item.description ?? undefined,
      });
    }
  }, [subData, addToast, t]);

  const folder = data?.account?.notification;

  const items = useMemo(() => {
    const byId = new Map<string, AccountNotification>();
    for (const n of folder?.paginatedList.items ?? []) byId.set(n.id, n);
    for (const n of pushed) if (!byId.has(n.id)) byId.set(n.id, n);

    return Array.from(byId.values())
      .map((n) => (locallyReadIds.has(n.id) ? { ...n, viewed: true } : n))
      .filter((n) => categorizeNotification(n) !== null)
      .sort((a, b) => timestampOf(b) - timestampOf(a));
  }, [folder, pushed, locallyReadIds]);

  // `unread` is the full unread set (not paginated) — the accurate source for the badge count
  // and for "mark all as read", since the recent-items page above may not include every unread
  // notification.
  const unreadIds = useMemo(() => {
    const known = folder?.unread ?? [];
    const pushedUnread = pushed.filter((n) => !n.viewed);
    const byId = new Map<string, AccountNotification>();
    for (const n of known) byId.set(n.id, n);
    for (const n of pushedUnread) if (!byId.has(n.id)) byId.set(n.id, n);

    return Array.from(byId.values())
      .filter((n) => !locallyReadIds.has(n.id))
      .filter((n) => categorizeNotification(n) !== null)
      .map((n) => n.id);
  }, [folder, pushed, locallyReadIds]);

  const markAsRead = useCallback(
    (ids: string[]) => {
      const pending = ids.filter((id) => !locallyReadIds.has(id));
      if (pending.length === 0) return;
      setLocallyReadIds((prev) => new Set([...prev, ...pending]));
      void markReadMutation({ ids: pending });
    },
    [locallyReadIds, markReadMutation],
  );

  const markAllAsRead = useCallback(() => {
    markAsRead(unreadIds);
  }, [markAsRead, unreadIds]);

  const refetch = useCallback(() => {
    reexecuteQuery({ requestPolicy: 'network-only' });
  }, [reexecuteQuery]);

  return {
    items,
    unreadCount: unreadIds.length,
    fetching,
    error,
    markAsRead,
    markAllAsRead,
    refetch,
  };
}

/**
 * The backend by default; `VITE_NOTIFICATIONS_MOCK=true` serves dev-only fixtures of the Vault
 * events instead (see `use-mock-account-notifications.ts`). Chosen once at module load, so each
 * render still calls the same hook.
 */
export const useAccountNotifications: () => AccountNotificationsState =
  import.meta.env.VITE_NOTIFICATIONS_MOCK === 'true'
    ? useMockAccountNotifications
    : useRemoteAccountNotifications;
